import React, { useContext } from 'react';

import { CompanyContext } from 'common/containers/CompanyContainer';
import NoticeBar from 'common/uiV3/Composite/NoticeBar';
import { canStartCoreTrial, canStartProTrial } from 'common/util/canStartTrial';
import devURL from 'common/util/devURL';
import isCore from 'common/util/isCore';
import isFree from 'common/util/isFree';
import isPro from 'common/util/isPro';
import numberWithCommas from 'common/util/numberWithCommas';

export default function AdminV2TrackedUserNotice() {
  const company = useContext(CompanyContext);
  if (!company?.viewerIsMember) {
    return null;
  } else if (
    !isFree(company.billingData?.plan?.planID) &&
    !isCore(company.billingData?.plan?.planID) &&
    !isPro(company.billingData?.plan?.planID)
  ) {
    return null;
  } else if (!company.limits?.users) {
    return null;
  }

  if (isFree(company.billingData?.plan?.planID)) {
    // free plan TU notice
    const limit = company.limits?.users;
    const trackedUsers = company.contributorCount;
    const canStartTrial =
      canStartCoreTrial(company.billingData?.plan?.planID, company.planTrial?.planID) ||
      canStartProTrial(company.billingData?.plan?.planID, company.planTrial?.planID);

    if (trackedUsers < limit / 2) {
      return null;
    } else if (trackedUsers < limit) {
      // you're getting close to the limit

      return (
        <NoticeBar
          variant="neutral"
          message={`You're at ${numberWithCommas(trackedUsers)} of your ${numberWithCommas(limit)} tracked user limit`}
          action={{
            type: 'link',
            label: canStartTrial ? 'Start trial' : 'Upgrade',
            to: '/admin/settings/billing/subscription',
          }}
        />
      );
    } else if (trackedUsers >= limit) {
      // you've hit the limit
      return (
        <NoticeBar
          variant="neutral"
          message={`You've hit the ${numberWithCommas(limit)} tracked user limit`}
          action={{ type: 'link', label: 'Upgrade', to: '/admin/settings/billing/subscription' }}
        />
      );
    }
  } else {
    // core/pro TU notice

    // only show notices for tiered plans
    const plan = company.billingData?.plan;
    if (!plan?.tiered || !plan?.priceTiers) {
      return null;
    }

    const setPlanTierLimit = company.billingData?.planTierLimit?.users;
    const maxLimit = plan?.priceTiers[plan.priceTiers.length - 1].users;

    const trackedUserLimit = setPlanTierLimit || maxLimit;
    const withinTenPercent = trackedUserLimit * 0.9;
    const withinFifty = trackedUserLimit - 50;
    const warningAmount = Math.min(withinTenPercent, withinFifty);

    const trackedUsers = company.contributorCount;

    const canIncreaseLimit = setPlanTierLimit && setPlanTierLimit < maxLimit;

    if (trackedUsers < warningAmount) {
      return null;
    } else if (trackedUsers < trackedUserLimit) {
      return (
        <NoticeBar
          variant="neutral"
          message={`You're at ${numberWithCommas(trackedUsers)} of your ${numberWithCommas(trackedUserLimit)} tracked user limit`}
          action={
            canIncreaseLimit
              ? { type: 'link', label: 'Upgrade', to: '/admin/settings/billing/subscription' }
              : {
                  type: 'link',
                  label: 'Request a limit increase',
                  isExternal: true,
                  href: devURL('https://canny.io/request-demo'),
                }
          }
        />
      );
    } else if (trackedUsers >= trackedUserLimit) {
      return (
        <NoticeBar
          variant="neutral"
          message={`You've hit your ${numberWithCommas(trackedUserLimit)} tracked user limit`}
          action={
            canIncreaseLimit
              ? { type: 'link', label: 'Upgrade', to: '/admin/settings/billing/subscription' }
              : {
                  type: 'link',
                  label: 'Request a limit increase',
                  isExternal: true,
                  href: devURL('https://canny.io/request-demo'),
                }
          }
        />
      );
    }
  }

  // should never get here, but makes ts happy
  return null;
}
